import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { FirebaseMessagingService, ForegroundMessage } from './firebase-messaging.service';
import { NotificationStateService } from './notification-state.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationToastService {
  private isInitialized = false;
  private currentAudio: HTMLAudioElement | null = null;
  private soundStopTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly MAX_SOUND_DURATION = 20000;
  private pendingInteractionAbort: AbortController | null = null;

  constructor(
    private firebaseMessagingService: FirebaseMessagingService,
    private notificationStateService: NotificationStateService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private router: Router
  ) {}

  /**
   * Initialize toast service
   * - Listen to foreground messages
   * - Display toast when notification arrives
   */
  initialize(): void {
    if (this.isInitialized) {
      console.log('[NotificationToast] Already initialized');
      return;
    }

    console.log('[NotificationToast] Initializing...');
    this.isInitialized = true;

    // Listen to foreground messages
    this.firebaseMessagingService.getForegroundMessages().subscribe((message) => {
      if (message) {
        this.showToast(message);
        // Refresh notification state to update badge count
        this.notificationStateService.refreshState();
      }
    });
  }

  /**
   * Show toast notification
   * @param message - Foreground message to display
   */
  private showToast(message: ForegroundMessage): void {
    const currentLang = this.translateService.currentLang || 'en';

    // Extract title and body based on current language
    // If message has bilingual data, use it; otherwise use the raw strings
    let title = message.title;
    let body = message.body;

    // If the data contains bilingual content, parse it
    if (message.data) {
      try {
        // Check if title is a JSON string with language keys
        if (message.data['title_en'] && message.data['title_ar']) {
          title = currentLang === 'ar' ? message.data['title_ar'] : message.data['title_en'];
        }
        if (message.data['body_en'] && message.data['body_ar']) {
          body = currentLang === 'ar' ? message.data['body_ar'] : message.data['body_en'];
        }
      } catch (error) {
        console.warn('[NotificationToast] Error parsing bilingual data:', error);
      }
    }

    this.messageService.add({
      severity: 'info',
      summary: title,
      detail: body,
      life: 335000,
      styleClass: 'notification-toast',
      data: {
        purchaseOrderId: message.data?.['purchaseOrderId']
      }
    });

    if (!document.hasFocus()) {
      // Browser is not focused — show native OS notification so the OS handles sound
      if (Notification.permission === 'granted') {
        const url = message.data?.['purchaseOrderId']
          ? `/orders/view/${message.data['purchaseOrderId']}`
          : '/notifications';
        const n = new Notification(title, {
          body,
          icon: '/assets/icons/notification-icon.png',
          data: { url }
        });
        n.onclick = () => {
          window.focus();
          this.router.navigate([url]);
          n.close();
        };
      }
    } else {
      this.playNotificationSound();
    }
  }

  /**
   * Play notification sound
   */
  playNotificationSound(): void {
    console.log('[NotificationToast] playNotificationSound called');
    this.stopNotificationSound();

    // Cancel any previously queued interaction-gated play
    if (this.pendingInteractionAbort) {
      this.pendingInteractionAbort.abort();
      this.pendingInteractionAbort = null;
    }

    try {
      const audio = new Audio('/assets/sounds/notification.mp3');
      audio.volume = 1;
      audio.loop = true;
      this.currentAudio = audio;

      audio.play()
        .then(() => {
          console.log('[NotificationToast] Sound playing');
          this.soundStopTimer = setTimeout(() => this.stopNotificationSound(), this.MAX_SOUND_DURATION);
        })
        .catch((error) => {
          if (error.name === 'NotAllowedError') {
            // Queue to play on next user interaction
            console.log('[NotificationToast] Autoplay blocked — queuing for next interaction');
            const abort = new AbortController();
            this.pendingInteractionAbort = abort;
            const playOnInteraction = () => {
              if (!abort.signal.aborted) {
                audio.play().catch(() => {});
                this.soundStopTimer = setTimeout(() => this.stopNotificationSound(), this.MAX_SOUND_DURATION);
              }
              this.pendingInteractionAbort = null;
            };
            ['click', 'keydown', 'touchstart'].forEach(evt =>
              document.addEventListener(evt, playOnInteraction, { once: true, capture: true, signal: abort.signal } as AddEventListenerOptions)
            );
            // Also trigger when the window regains focus (e.g. seller clicks OS notification)
            window.addEventListener('focus', playOnInteraction, { once: true, signal: abort.signal } as AddEventListenerOptions);
          } else {
            console.warn('[NotificationToast] Could not play notification sound:', error);
          }
        });
    } catch (error) {
      console.warn('[NotificationToast] Error playing notification sound:', error);
    }
  }

  stopNotificationSound(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    if (this.soundStopTimer) {
      clearTimeout(this.soundStopTimer);
      this.soundStopTimer = null;
    }
  }

  /**
   * Handle toast click (navigate to order)
   * @param purchaseOrderId - Order ID to navigate to
   */
  navigateToOrder(purchaseOrderId: string): void {
    if (purchaseOrderId) {
      this.router.navigate(['/orders', purchaseOrderId]);
    }
  }
}
